/**
 * Top-nav live "Players Online" pill.
 *
 * Polls /api/servers/status every 30s, sums player_count across all
 * configured servers, and shows a small clickable badge:
 *
 *     🟢  3 online    →  /map
 *
 * Renders nothing while loading or on error so the nav stays clean.
 * Click navigates to /map (which has the live player overlay) so users
 * can see *who* is online, not just the count.
 *
 * Visible on every page via Layout — answers the "is anyone playing
 * right now?" question without scrolling to the home page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CircleDot } from "lucide-react";

import { getShared } from "@/lib/api";

const POLL_MS = 30_000;

export function OnlinePlayersPill() {
  const [count, setCount] = useState(null);
  const [anyOnline, setAnyOnline] = useState(false);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const data = await getShared("/servers/status");
        if (!alive) return;
        const servers = data?.servers || [];
        const online = servers.some((s) => s.online);
        const total = servers.reduce((acc, s) => acc + (s.player_count || 0), 0);
        setCount(total);
        setAnyOnline(online);
      } catch {
        // Silent — nav stays clean if the endpoint is down
      }
    };
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  if (count === null) return null;

  const dotColor = anyOnline && count > 0 ? "#34d399" : "#666";
  const label = `${count} online`;

  return (
    <Link
      to="/map"
      data-testid="online-players-pill"
      title="Live player count across both servers — click for live map"
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-black/30 hover:border-[var(--teal-soft)] hover:bg-[rgba(0,229,255,0.06)] transition-colors"
    >
      <CircleDot
        size={10}
        className={anyOnline && count > 0 ? "animate-pulse" : ""}
        style={{ color: dotColor }}
      />
      <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--bone)]">
        {label}
      </span>
    </Link>
  );
}
